<script setup lang="ts">
import type { StrongsEntry } from '../../types/strongs'

interface Props {
  entry: StrongsEntry | null
  loading?: boolean
  error?: string | null
}

interface Emits {
  (e: 'select-related', number: string, testament: 'OT' | 'NT', word: string): void
}

defineProps<Props>()
const emit = defineEmits<Emits>()

const displayChineseDefinition = (def: string) => {
  return def.split('\r\n\r\n').filter(s => s.trim())
}

const handleSelectRelated = (number: string) => {
  const props = defineProps<Props>()
  if (props.entry) {
    emit('select-related', number, props.entry.testament, number)
  }
}
</script>

<template>
  <div class="strongs-panel-content">
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <span>查詢中...</span>
    </div>

    <div v-else-if="error" class="error-state">
      <span class="error-message">{{ error }}</span>
    </div>

    <div v-else-if="entry" class="entry-content">
      <div v-if="entry.originalWord" class="section">
        <div class="label">原文</div>
        <div class="value">{{ entry.originalWord.split('\n')[0] }}</div>
      </div>

      <div class="section">
        <div class="label">編號</div>
        <div class="value">{{ entry.testament === 'OT' ? 'H' : 'G' }}{{ entry.strongsNumber }}</div>
      </div>

      <div v-if="entry.chineseDefinition" class="section">
        <div class="label">中文釋義</div>
        <div class="definition">
          <p v-for="(para, idx) in displayChineseDefinition(entry.chineseDefinition)" :key="`ch-${idx}`">
            {{ para }}
          </p>
        </div>
      </div>

      <details v-if="entry.englishDefinition" class="section">
        <summary class="label">英文釋義</summary>
        <div class="definition">
          <p v-for="(para, idx) in displayChineseDefinition(entry.englishDefinition)" :key="`en-${idx}`">
            {{ para }}
          </p>
        </div>
      </details>

      <div v-if="entry.relatedWords && entry.relatedWords.length > 0" class="section">
        <div class="label">相關字</div>
        <div class="related-words">
          <button
            v-for="word in entry.relatedWords"
            :key="word.number"
            class="related-word-btn"
            @click="handleSelectRelated(word.number)"
          >
            <span class="word-text">{{ word.word }}</span>
            <span class="word-number">#{{ word.number }}</span>
            <span v-if="word.occurrences" class="word-count">({{ word.occurrences }})</span>
          </button>
        </div>
      </div>
    </div>

    <div v-else class="empty-state">
      <span>未選擇強字號</span>
    </div>
  </div>
</template>

<style scoped>
.strongs-panel-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.loading-state,
.error-state,
.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 20px;
  color: #666;
  min-height: 100px;
}

.error-message {
  color: #d32f2f;
  font-size: 12px;
}

.entry-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.section {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.label {
  font-size: 12px;
  font-weight: 600;
  color: #1f2937;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.value {
  font-size: 14px;
  color: #111;
  white-space: normal;
  word-break: break-word;
}

.definition {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.definition p {
  font-size: 13px;
  line-height: 1.5;
  color: #333;
  margin: 0;
}

details {
  cursor: pointer;
}

details > summary {
  outline: none;
  user-select: none;
}

details[open] > summary {
  margin-bottom: 6px;
}

.related-words {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.related-word-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  background-color: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
  text-align: left;
}

.related-word-btn:hover {
  background-color: #e5e7eb;
  border-color: #d1d5db;
}

.word-text {
  font-weight: 500;
  color: #111;
  flex: 1;
}

.word-number {
  color: #666;
  font-size: 11px;
}

.word-count {
  color: #999;
  font-size: 11px;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
